/**
 * Inline confirmation card rendered inside the Alice chat panel after the
 * model proposes a write tool call. The user can approve or discard; the
 * panel handles the actual write and tool-loop continuation.
 */

import { AlertCircle, Check, Loader2, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import type { CopilotPendingConfirmation } from '@/lib/api';
import {
  approvalRationale,
  formatApprovalValue,
  getApprovalFieldLabel,
  getApprovalPatch,
} from '@/lib/copilot-approvals';

interface CopilotWriteConfirmCardProps {
  pending: CopilotPendingConfirmation;
  onApply: () => void | Promise<void>;
  onDiscard: () => void | Promise<void>;
  busy?: boolean;
  errorMessage?: string | null;
}

export function CopilotWriteConfirmCard({
  pending,
  onApply,
  onDiscard,
  busy = false,
  errorMessage,
}: CopilotWriteConfirmCardProps) {
  const patch = getApprovalPatch(pending);
  const rationale = approvalRationale(pending);
  const entries = Object.entries(patch);

  return (
    <Card className="space-y-3 border-primary/30 bg-primary/[0.04] p-3">
      <div className="flex items-center gap-2 text-[12px] font-semibold text-primary">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        Alice wants to apply this update - review before approving
      </div>

      {pending.summary && (
        <div className="rounded-md border border-border bg-card px-2.5 py-2 text-[12px] whitespace-pre-wrap text-foreground">
          {pending.summary}
        </div>
      )}

      {entries.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Proposed changes
          </div>
          <ul className="space-y-1">
            {entries.map(([key, value]) => (
              <li
                key={key}
                className="flex flex-wrap items-baseline gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-[12px]"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {getApprovalFieldLabel(key)}
                </span>
                <span className="break-words text-foreground">{formatApprovalValue(value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {rationale && (
        <div className="text-[11px] italic text-muted-foreground">
          Rationale: {rationale}
        </div>
      )}

      {errorMessage && (
        <div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-2 text-[12px] text-destructive">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-1.5 pt-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => onDiscard()}
          disabled={busy}
          className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          Discard
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => onApply()}
          disabled={busy}
          className="h-8 gap-1.5"
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Check className="h-3.5 w-3.5" aria-hidden />
          )}
          Approve
        </Button>
      </div>
    </Card>
  );
}
